import { useTranslation } from 'react-i18next';

import { ApiError } from '@/lib/api-error';

/**
 * Turns whatever a mutation threw into something worth showing a person.
 *
 * The API's error envelope carries an array of messages; joining them keeps
 * server-side detail the client cannot compute — "password is too easy to
 * guess", "an account with that email already exists" — instead of flattening
 * everything to "Something went wrong".
 *
 * A 5xx is the exception: whatever it says is about the server, not about what
 * the reader typed, and it can carry stack traces or connection strings. Those
 * get one fixed sentence.
 *
 * A hook rather than a plain function because the messages are translated
 * (spec §8.9); it returns a function, so it can still be called from an event
 * handler rather than only during render.
 */
export function useErrorMessage(): (error: unknown, fallback: string) => string {
  const { t } = useTranslation();

  return (error, fallback) => {
    if (error instanceof ApiError) {
      if (error.statusCode >= 500) return t('errors.server');
      if (error.messages.length > 0) return error.messages.join(' ');
    }

    if (error instanceof Error && error.message) return error.message;

    return fallback;
  };
}
